import React from "react";
import { Backdrop, Button, CircularProgress, Typography } from "@mui/material";
import type { PointsMaterialProps, PointsProps } from "@react-three/fiber";
import { Canvas } from "@react-three/fiber";
import { TimerSand } from "mdi-material-ui";
import { Center } from "~/components/Center";
import { Loading } from "~/components/Loading";
import { ErrorMessage } from "~/components/error-message";
import type { Topic } from "~/lqs";
import { usePlayerActions } from "../../actions";
import PanelLayout from "../../components/PanelLayout";
import { useUpdatePanelBuffering } from "../../hooks";
import type { InitializedPanelNode } from "../../panels";
import { CameraControls } from "./ThreeDVisualizationControls";
import { useThreeDRecords } from "./use-three-d-records";

declare module "react" {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      points: PointsProps;
      pointsMaterial: PointsMaterialProps;
    }
  }
}

export function ThreeDVisualization({
  panel,
  topic,
}: {
  panel: InitializedPanelNode;
  topic: Topic;
}) {
  const playerActions = usePlayerActions();

  const { snapshot, isPlaceholder } = useThreeDRecords(topic);

  useUpdatePanelBuffering(snapshot.status === "pending" || isPlaceholder);

  let content;
  if (snapshot.status === "pending") {
    content = <Loading type="circular" />;
  } else if (snapshot.status === "rejected") {
    content = <ErrorMessage>An error occurred</ErrorMessage>;
  } else {
    const mostRecentRecord = snapshot.value.findLast(
      (record) => record.timestamp <= snapshot.request.timestamp,
    );

    let firstRecordSkipTimestamp: bigint | undefined = undefined;
    if (mostRecentRecord === undefined) {
      firstRecordSkipTimestamp = snapshot.value.at(0)?.timestamp;
    }

    content = (
      <>
        <Canvas frameloop="demand" scene={{ rotation: [Math.PI, 0, 0] }}>
          {mostRecentRecord?.data != null && (
            /* eslint-disable react/no-unknown-property */
            <points geometry={mostRecentRecord.data}>
              <pointsMaterial vertexColors size={panel.pointCloudPointSize} />
            </points>
            /* eslint-enable react/no-unknown-property */
          )}
          <CameraControls panel={panel} />
        </Canvas>
        {firstRecordSkipTimestamp !== undefined && (
          <Center sx={{ position: "absolute", left: 0, top: 0 }}>
            <TimerSand fontSize="large" />
            <Typography variant="h5" component="p" paragraph>
              No recent point cloud
            </Typography>
            <Button
              color="primary"
              variant="outlined"
              onClick={() => playerActions.seek(firstRecordSkipTimestamp)}
            >
              Skip to First Point Cloud
            </Button>
          </Center>
        )}
      </>
    );
  }

  return (
    <PanelLayout>
      {content}
      {isPlaceholder && (
        <Backdrop open sx={{ position: "absolute" }}>
          <CircularProgress />
        </Backdrop>
      )}
    </PanelLayout>
  );
}
